import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import styles from "./EditAccountModalComponent.module.css";

/** Slugify a string */
function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Normalize Firestore currencies */
function normalizeCurrencies(raw = []) {
  return raw
    .map((c) => ({
      code: String(c.currencyCode || c.code || "").trim(),
      name: String(c.currencyName || "").trim(),
      symbol: String(c.symbol || "").trim(),
    }))
    .filter((c) => c.code || c.name);
}

/** Resolve initial currency code */
function resolveInitialCurrencyCode(rowCodeOrName, currencyList) {
  const v = String(rowCodeOrName || "").trim();
  if (!v) return "";
  if (currencyList.some((c) => c.code === v)) return v;
  const found = currencyList.find((c) => c.name === v);
  return found?.code || v;
}

/** Pretty label from key */
function labelFromKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (m) => m.toUpperCase());
}

function cloneSpecialsFromType(t) {
  if (!t || !Array.isArray(t.special_attributes)) return [];
  return t.special_attributes.map((sa) => ({ key: sa.key, value: sa.value ?? "" }));
}

export default function EditAccountModalComponent({
  row,
  onClose,
  onSave,
  accountTypes,
  currencies,
}) {
  const currencyList = useMemo(() => normalizeCurrencies(currencies), [currencies]);
  const currencyByCode = useMemo(
    () => new Map(currencyList.map((c) => [c.code, c])),
    [currencyList]
  );
  const typesById = useMemo(
    () => Object.fromEntries((accountTypes || []).map((t) => [t.id, t])),
    [accountTypes]
  );

  const [saving, setSaving] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const [form, setForm] = useState(() => ({
    // required
    name: row?.name || "",
    currencyCode: resolveInitialCurrencyCode(row?.currencyCode, currencyList),
    systemTypeId: row?.systemTypeId || "",
    systemTypeName: row?.systemTypeName || "",

    // optional
    systemTypeDesc: row?.systemTypeDesc || "",
    accountBalance: row?.accountBalance ?? 0,
    special_attributes: Array.isArray(row?.special_attributes) ? row.special_attributes : [],
  }));

  // keep currencyCode in sync if currencies load later
  useEffect(() => {
    setForm((f) => ({
      ...f,
      currencyCode:
        f.currencyCode || resolveInitialCurrencyCode(row?.currencyCode, currencyList),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyList.length]);

  // derived
  const currencyMeta = form.currencyCode ? currencyByCode.get(form.currencyCode) : null;
  const currencySymbol = currencyMeta?.symbol || "";

  const errors = {
    name: !form.name.trim(),
    currencyCode: !form.currencyCode,
    systemTypeId: !form.systemTypeId,
  };
  const isValid = !errors.name && !errors.currencyCode && !errors.systemTypeId;

  // Warn + replace optional fields when account type changes
  const onTypeChange = (id) => {
    if (!id || id === form.systemTypeId) return;
    const t = typesById[id];

    // Simple confirm; replace with a prettier modal if you want
    const proceed = window.confirm(
      "Changing the account type is a major change.\n\n" +
      "If you continue, the Type Description and any Special Fields " +
      "will be replaced with the defaults for the selected type.\n\n" +
      "Do you want to continue?"
    );
    if (!proceed) return;

    setForm((f) => ({
      ...f,
      systemTypeId: id,
      systemTypeName: t?.name || id,
      systemTypeDesc: t?.desc || "",
      special_attributes: cloneSpecialsFromType(t),
    }));

    // If the new type has specials, surface the optional section automatically
    if (Array.isArray(t?.special_attributes) && t.special_attributes.length > 0) {
      setShowOptional(true);
    }
  };

  const onSpecialAttrChange = (key, value) => {
    setForm((f) => ({
      ...f,
      special_attributes: (Array.isArray(f.special_attributes) ? f.special_attributes : []).map(
        (sa) => (sa.key === key ? { ...sa, value } : sa)
      ),
    }));
  };

  const handleSave = async () => {
    if (!isValid) return;

    // Only build "updates" (parent attaches id)
    const updates = {
      name: form.name.trim(),
      slug: slugify(form.name),
      currencyCode: form.currencyCode,
      systemTypeId: form.systemTypeId,
      systemTypeName: form.systemTypeName || form.systemTypeId,
      systemTypeDesc: form.systemTypeDesc || "",
      accountBalance:
        form.accountBalance === "" || form.accountBalance === null
          ? 0
          : Number(form.accountBalance) || 0,
      special_attributes: Array.isArray(form.special_attributes)
        ? form.special_attributes
        : [],
    };

    try {
      setSaving(true);
      await onSave?.(updates);   // parent handles id + updateUserAccount
      setSaving(false);
      onClose?.();
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  };

  const handleOverlayMouseDown = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    body.classList.add("modal-open");
    body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      body.classList.remove("modal-open");
      body.style.overflow = prevOverflow || "";
    };
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="modal fade show d-block pp-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onMouseDown={handleOverlayMouseDown}
        style={{ position: "fixed", inset: 0, zIndex: 1080 }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Account</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>

            <div className="modal-body">
              <div className="small text-secondary mb-2">
                <span className="text-danger">*</span> Required fields
              </div>

              <div className="row g-3">
                {/* Name */}
                <div className="col-md-6">
                  <label className="form-label">Name *</label>
                  <input
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">Name is required.</div>
                  )}
                </div>

                {/* Currency */}
                <div className="col-md-3">
                  <label className="form-label">Currency *</label>
                  <select
                    className={`form-select ${errors.currencyCode ? "is-invalid" : ""}`}
                    value={form.currencyCode}
                    onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}
                  >
                    <option value="">— Select —</option>
                    {currencyList.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Balance */}
                <div className="col-md-3">
                  <label className="form-label">Balance</label>
                  <div className="input-group">
                    {currencySymbol && (
                      <span className="input-group-text">{currencySymbol}</span>
                    )}
                    <input
                      type="number"
                      className="form-control"
                      value={form.accountBalance ?? ""}
                      onChange={(e) => setForm({ ...form, accountBalance: e.target.value })}
                    />
                  </div>
                </div>

                {/* Account Type */}
                <div className="col-md-6">
                  <label className="form-label">Account Type *</label>
                  <select
                    className={`form-select ${errors.systemTypeId ? "is-invalid" : ""}`}
                    value={form.systemTypeId}
                    onChange={(e) => onTypeChange(e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {(accountTypes || []).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <div className="form-text">
                    Changing the account type will replace description & special fields with defaults.
                  </div>
                </div>

                {/* Optional toggle */}
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowOptional((v) => !v)}
                  >
                    {showOptional ? "Hide optional fields" : "Show optional fields"}
                  </button>
                </div>

                {showOptional && (
                  <div className="row g-3 mt-2">
                    {/* Type Description */}
                    <div className="col-md-12">
                      <label className="form-label">Type Description</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.systemTypeDesc}
                        onChange={(e) => setForm({ ...form, systemTypeDesc: e.target.value })}
                      />
                    </div>

                    {/* Special attributes */}
                    {form.special_attributes?.length > 0 && (
                      <div className="col-12">
                        <div className="row g-3">
                          {form.special_attributes.map((sa) => (
                            <div className="col-md-4" key={sa.key}>
                              <label className="form-label">{labelFromKey(sa.key)}</label>
                              <input
                                type="text"
                                className="form-control"
                                value={sa.value ?? ""}
                                onChange={(e) =>
                                  onSpecialAttrChange(sa.key, e.target.value)
                                }
                              />
                            </div>
                          ))}
                        </div>
                        <div className="form-text">
                          Special fields come from the selected account type.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !isValid}
              >
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal-backdrop fade show pp-modal-backdrop"
        style={{ zIndex: 1070 }}
        onMouseDown={onClose}
      />
    </>,
    document.body
  );
}

EditAccountModalComponent.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  accountTypes: PropTypes.array,
  currencies: PropTypes.array,
};
